import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import orangeLogo from '../assets/orangelog.png';
import AdSlot from '../components/AdSlot';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

const GENRE_COLOR = {
  'National News': '#dd3333',
  'State News':    '#4db2ec',
  'Local News':    '#27ae60',
  'Sports':        '#e67e22',
  'Technology':    '#8e44ad',
};

const GENRE_ROUTES = [
  { label: 'National News', to: '/national-news', color: '#dd3333' },
  { label: 'State News',    to: '/state-news',    color: '#4db2ec' },
  { label: 'Sports',        to: '/sports',        color: '#e67e22' },
  { label: 'Technology',    to: '/technology',    color: '#8e44ad' },
  { label: 'Local News',    to: '/local-news',    color: '#27ae60' },
];

const LOCAL_COLOR = '#27ae60';

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ArticleCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${T.border}` }}
    >
      {article.headline_image && (
        <div style={{ height: '180px', overflow: 'hidden', marginBottom: '10px' }}>
          <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s', transform: hov ? 'scale(1.03)' : 'scale(1)' }} />
        </div>
      )}
      <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '5px', fontFamily: 'Arial, sans-serif' }}>
        {article.genre_tree?.main}
      </div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '26px', marginBottom: '6px', transition: 'color 0.13s', fontFamily: 'Georgia, serif' }}>
        {article.title}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {article.hero_image && (
          <img src={article.hero_image} alt={article.company_name} style={{ height: '16px', width: '16px', borderRadius: '50%', objectFit: 'cover' }} />
        )}
        <span style={{ fontSize: '12px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </span>
        {article.distance_km !== null && article.distance_km !== undefined && (
          <span style={{ fontSize: '11px', color: LOCAL_COLOR, fontWeight: '600', fontFamily: 'Arial, sans-serif' }}>
            {article.distance_km < 1 ? '< 1 mi' : `${(article.distance_km / 1.60934).toFixed(0)} mi away`}
          </span>
        )}
      </div>
    </div>
  );
}

function LocalNews() {
  const navigate = useNavigate();
  const { accessToken: token } = useAuth();
  const userType = localStorage.getItem('userType');
  const username = localStorage.getItem('username');

  const [articles,      setArticles]      = useState([]);
  const [companies,     setCompanies]     = useState([]);
  const [hasLocation,   setHasLocation]   = useState(true);
  const [loading,       setLoading]       = useState(true);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [genreFilter,   setGenreFilter]   = useState('all');
  const [isMobile,      setIsMobile]      = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!token || userType !== 'user') { navigate('/login'); return; }
    axios.get('/api/articles/local', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setArticles(res.data.articles   || []);
        setCompanies(res.data.companies || []);
        setHasLocation(res.data.hasLocation !== false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const availableGenres = useMemo(() => {
    const seen = new Set();
    articles.forEach(a => { if (a.genre_tree?.main) seen.add(a.genre_tree.main); });
    return [...seen].sort();
  }, [articles]);

  const displayArticles = useMemo(() => {
    return articles.filter(a => {
      if (companyFilter !== 'all' && String(a.company_id) !== companyFilter) return false;
      if (genreFilter   !== 'all' && a.genre_tree?.main !== genreFilter)       return false;
      return true;
    });
  }, [articles, companyFilter, genreFilter]);

  const handleArticleClick = (article) => {
    navigate(`/${encodeURIComponent(article.company_name)}?a=${article._id}`);
  };

  const siloHref = token && userType === 'user' ? `/${username}` : null;

  return (
    <>
    <Helmet>
      <title>Local News — PubSilo</title>
      <meta name="description" content="Local journalism from publishers in your area — curated on PubSilo." />
    </Helmet>
    <div style={{ minHeight: '100vh', background: T.white, fontFamily: 'Georgia, serif', color: T.bodyText }}>

      {/* Navbar */}
      {isMobile ? (
        <nav style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ display: 'flex', height: '70px' }}>
            <div style={{ width: '50%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
            </div>
            <div style={{ width: '50%', background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <div style={{ fontSize: '9px', fontWeight: '900', letterSpacing: '0.1em', textTransform: 'uppercase', color: T.white, background: LOCAL_COLOR, padding: '2px 8px', fontFamily: 'Arial, sans-serif' }}>Section</div>
              <div style={{ color: T.white, fontSize: '18px', fontWeight: '900', fontFamily: 'Georgia, serif', lineHeight: 1 }}>Local News</div>
            </div>
          </div>
          <div style={{ background: '#0d1f33', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', borderTop: '1px solid #1a2f45' }}>
            {token && userType === 'user' ? (
              <Link to={`/${username}`} style={{ background: T.accent, color: T.white, padding: '6px 14px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: '700', fontFamily: 'Arial, sans-serif' }}>
                My Silo
              </Link>
            ) : (
              <>
                <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 12px', border: '1px solid #3a3a3a', borderRadius: '4px' }}>Log In</Link>
                <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 14px', borderRadius: '4px' }}>Sign Up</Link>
              </>
            )}
          </div>
        </nav>
      ) : (
        <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '900', letterSpacing: '0.1em', textTransform: 'uppercase', color: T.white, background: LOCAL_COLOR, padding: '3px 10px', fontFamily: 'Arial, sans-serif' }}>Section</div>
            <div style={{ color: T.white, fontSize: '30px', fontWeight: '900', fontFamily: 'Georgia, serif', lineHeight: 1 }}>Local News</div>
          </div>
          <div style={{ gridColumn: 2, gridRow: 2, background: '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {GENRE_ROUTES.map(({ label, to, color }) => (
              <Link key={label} to={to}
                style={{ color: label === 'Local News' ? T.white : '#aaa', textDecoration: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 16px', height: '100%', display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', background: label === 'Local News' ? LOCAL_COLOR : 'transparent', transition: 'background 0.12s, color 0.12s' }}
                onMouseEnter={e => { if (label !== 'Local News') { e.currentTarget.style.background = color; e.currentTarget.style.color = T.white; } }}
                onMouseLeave={e => { if (label !== 'Local News') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; } }}
              >{label}</Link>
            ))}
          </div>
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            {siloHref && (
              <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '9px 18px', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap' }}>My Silo</Link>
            )}
          </div>
        </nav>
      )}

      {/* Body */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        {/* Left sidebar: nearby publishers */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '16px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', paddingBottom: '8px', borderBottom: `2px solid ${LOCAL_COLOR}`, marginBottom: '12px', fontFamily: 'Arial, sans-serif' }}>
            Nearby Publishers
          </div>
          {loading ? (
            [1,2,3].map(n => <div key={n} style={{ height: '44px', background: T.lightBg, borderRadius: '4px', marginBottom: '4px' }} />)
          ) : companies.length === 0 ? (
            <p style={{ fontSize: '12px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>No local publishers found near you yet.</p>
          ) : (
            <>
              <button
                onClick={() => setCompanyFilter('all')}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: '3px', borderRadius: '4px', border: 'none', fontSize: '13px', fontWeight: companyFilter === 'all' ? '800' : '500', fontFamily: 'Arial, sans-serif', color: companyFilter === 'all' ? T.white : T.bodyText, background: companyFilter === 'all' ? LOCAL_COLOR : 'transparent', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => { if (companyFilter !== 'all') e.currentTarget.style.background = T.lightBg; }}
                onMouseLeave={e => { if (companyFilter !== 'all') e.currentTarget.style.background = 'transparent'; }}
              >
                All Sources
              </button>
              {companies.map(c => (
                <button
                  key={String(c._id)}
                  onClick={() => setCompanyFilter(String(c._id))}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: '3px', borderRadius: '4px', border: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer', background: companyFilter === String(c._id) ? LOCAL_COLOR : 'transparent', color: companyFilter === String(c._id) ? T.white : T.bodyText, transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (companyFilter !== String(c._id)) e.currentTarget.style.background = T.lightBg; }}
                  onMouseLeave={e => { if (companyFilter !== String(c._id)) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  <div style={{ fontSize: '10px', color: companyFilter === String(c._id) ? 'rgba(255,255,255,0.7)' : T.meta, marginTop: '1px' }}>
                    {c.km < 1 ? '< 1 mi away' : `${(c.km / 1.60934).toFixed(0)} mi away`}
                  </div>
                </button>
              ))}
            </>
          )}
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${T.border}` }}>
            <Link to="/" style={{ display: 'block', textAlign: 'center', padding: '8px', background: T.lightBg, color: T.bodyText, textDecoration: 'none', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', borderRadius: '4px', fontFamily: 'Arial, sans-serif' }}>
              ← All News
            </Link>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0, padding: '24px 28px', borderRight: `1px solid ${T.border}` }}>
          <div style={{ borderBottom: `3px solid ${LOCAL_COLOR}`, paddingBottom: '8px', marginBottom: '16px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h1 style={{ fontSize: '20px', fontWeight: '900', color: T.headText, margin: 0, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Local News</h1>
            {!loading && companies.length > 0 && (
              <span style={{ fontSize: '12px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
                {companies.length} publisher{companies.length !== 1 ? 's' : ''} near you
              </span>
            )}
          </div>

          {/* Genre filter pills */}
          {!loading && availableGenres.length > 0 && (
            <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', marginBottom: '20px' }}>
              <button
                onClick={() => setGenreFilter('all')}
                style={{ padding: '4px 12px', borderRadius: '20px', border: genreFilter === 'all' ? `1.5px solid ${LOCAL_COLOR}` : '1.5px solid #ddd', background: genreFilter === 'all' ? LOCAL_COLOR : T.white, color: genreFilter === 'all' ? T.white : T.bodyText, fontSize: '11px', fontWeight: genreFilter === 'all' ? '700' : '400', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}
              >
                All Topics
              </button>
              {availableGenres.map(g => (
                <button
                  key={g}
                  onClick={() => setGenreFilter(g)}
                  style={{ padding: '4px 12px', borderRadius: '20px', border: genreFilter === g ? `1.5px solid ${GENRE_COLOR[g] || LOCAL_COLOR}` : '1.5px solid #ddd', background: genreFilter === g ? (GENRE_COLOR[g] || LOCAL_COLOR) : T.white, color: genreFilter === g ? T.white : T.bodyText, fontSize: '11px', fontWeight: genreFilter === g ? '700' : '400', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}
                >
                  {g}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem', fontFamily: 'Arial, sans-serif' }}>Loading…</div>
          ) : !hasLocation ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ fontSize: '15px', color: T.headText, fontWeight: '700', fontFamily: 'Arial, sans-serif', marginBottom: '8px' }}>No location on file.</p>
              <p style={{ fontSize: '13px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>Add a ZIP code to your account to see local news.</p>
            </div>
          ) : companies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ fontSize: '15px', color: T.headText, fontFamily: 'Arial, sans-serif' }}>No local publishers found near you yet.</p>
            </div>
          ) : displayArticles.length === 0 ? (
            <div style={{ textAlign: 'center', color: T.meta, padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
              No articles match this filter.
            </div>
          ) : (
            <>
              <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '16px', fontFamily: 'Arial, sans-serif' }}>
                {displayArticles.length} article{displayArticles.length !== 1 ? 's' : ''}
              </div>
              {displayArticles.flatMap((article, i) => {
                const card = <ArticleCard key={article._id} article={article} onClick={() => handleArticleClick(article)} />;
                if (i === 3 && isMobile) {
                  return [card, <AdSlot key="mobile-ad-local" genre="Local News" audience="local" mobile />];
                }
                return [card];
              })}
            </>
          )}
        </div>

        {/* Right sidebar — ad zone (desktop only) */}
        {!isMobile && (
          <div style={{ width: '300px', flexShrink: 0, minHeight: 'calc(100vh - 146px)', padding: '16px 12px' }}>
            <AdSlot genre="Local News" audience="local" />
            <div style={{ marginTop: '20px', background: T.lightBg, borderRadius: '6px', padding: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: T.headText, marginBottom: '6px', fontFamily: 'Arial, sans-serif' }}>Browse Other Sections</div>
              {GENRE_ROUTES.filter(r => r.label !== 'Local News').map(({ label, to, color }) => (
                <Link key={label} to={to}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', textDecoration: 'none', borderBottom: `1px solid ${T.border}`, fontSize: '12px', color: T.bodyText, fontFamily: 'Arial, sans-serif' }}
                  onMouseEnter={e => e.currentTarget.style.color = color}
                  onMouseLeave={e => e.currentTarget.style.color = T.bodyText}
                >
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
    </>
  );
}

export default LocalNews;
